/**
 * Armazenamento do login de demonstração (ADR-009): IndexedDB `rota-demo-auth` no navegador e um
 * mapa em memória nos testes (o jsdom não tem IndexedDB) ou quando o IndexedDB está bloqueado
 * (janela privada antiga, política do navegador). Nesse caso o acesso vale só até fechar a aba.
 *
 * O que fica guardado: pessoas (sem senha), o registro PBKDF2 (sal, iterações e hash), convites,
 * sessões e pedidos de redefinição (só o SHA-256 dos tokens), a espera progressiva por e-mail e a
 * auditoria com cadeia de hash. Nada disso vai para o `localStorage`.
 */

export type AuthStoreName = 'members' | 'invites' | 'sessions' | 'resets' | 'throttle' | 'audit' | 'meta';

const STORES: readonly AuthStoreName[] = [
  'members',
  'invites',
  'sessions',
  'resets',
  'throttle',
  'audit',
  'meta',
];

export interface AuthStorage {
  readonly kind: 'indexeddb' | 'memory';
  get<T>(store: AuthStoreName, key: string): Promise<T | undefined>;
  put<T>(store: AuthStoreName, key: string, value: T): Promise<void>;
  delete(store: AuthStoreName, key: string): Promise<void>;
  all<T>(store: AuthStoreName): Promise<T[]>;
  clear(): Promise<void>;
}

export const AUTH_DB_NAME = 'rota-demo-auth';
const DB_VERSION = 1;

/** Em memória (testes e navegadores sem IndexedDB). Clona os valores, como o IndexedDB. */
export function memoryStorage(): AuthStorage {
  const data = new Map<AuthStoreName, Map<string, unknown>>(STORES.map((s) => [s, new Map()]));
  const tbl = (s: AuthStoreName) => data.get(s) as Map<string, unknown>;
  return {
    kind: 'memory',
    async get<T>(s: AuthStoreName, k: string) {
      const v = tbl(s).get(k);
      return v === undefined ? undefined : (structuredClone(v) as T);
    },
    async put<T>(s: AuthStoreName, k: string, v: T) {
      tbl(s).set(k, structuredClone(v));
    },
    async delete(s: AuthStoreName, k: string) {
      tbl(s).delete(k);
    },
    async all<T>(s: AuthStoreName) {
      return [...tbl(s).values()].map((v) => structuredClone(v) as T);
    },
    async clear() {
      for (const m of data.values()) m.clear();
    },
  };
}

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

function openDb(name: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(name, DB_VERSION);
    open.onupgradeneeded = () => {
      const db = open.result;
      for (const s of STORES) if (!db.objectStoreNames.contains(s)) db.createObjectStore(s);
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
    open.onblocked = () => reject(new Error('IndexedDB bloqueado por outra aba.'));
  });
}

/** IndexedDB com uma conexão preguiçosa (aberta na primeira operação). */
export function indexedDbStorage(name = AUTH_DB_NAME): AuthStorage {
  let db: Promise<IDBDatabase> | null = null;
  const conn = () => {
    db ??= openDb(name).catch((err: unknown) => {
      db = null;
      throw err;
    });
    return db;
  };
  const tx = async (s: AuthStoreName, mode: IDBTransactionMode) =>
    (await conn()).transaction(s, mode).objectStore(s);
  return {
    kind: 'indexeddb',
    async get<T>(s: AuthStoreName, k: string) {
      return (await req((await tx(s, 'readonly')).get(k))) as T | undefined;
    },
    async put<T>(s: AuthStoreName, k: string, v: T) {
      await req((await tx(s, 'readwrite')).put(v, k));
    },
    async delete(s: AuthStoreName, k: string) {
      await req((await tx(s, 'readwrite')).delete(k));
    },
    async all<T>(s: AuthStoreName) {
      return (await req((await tx(s, 'readonly')).getAll())) as T[];
    },
    async clear() {
      for (const s of STORES) await req((await tx(s, 'readwrite')).clear());
    },
  };
}

/** IndexedDB quando existe; senão, memória. */
export function defaultStorage(): AuthStorage {
  return typeof indexedDB === 'undefined' ? memoryStorage() : indexedDbStorage();
}
